/**
 * App shell: routing + shared chrome (AppLayout) + auth gate (RequireAuth).
 * Global sidebar (tree) and Cmd-K search are mounted here for every signed-in page.
 */
import {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import { Link, Navigate, Outlet, Route, Routes, useLocation } from 'react-router-dom';
import { BookOpen, ChevronDown, LogOut, Shield } from 'lucide-react';
import { useAuth } from '@/context/AuthContext';
import { TreeProvider } from '@/context/TreeContext';
import Login from '@/pages/Login';
import ForgotPassword from '@/pages/ForgotPassword';
import ResetPassword from '@/pages/ResetPassword';
import Feed from '@/pages/Feed';
import DocView from '@/pages/DocView';
import DocEdit from '@/pages/DocEdit';
import Admin from '@/pages/Admin';
import Sidebar from '@/components/Sidebar';
import { SearchPalette, SearchTrigger } from '@/components/SearchPalette';

/* ── Layout slots: let a page override the sidebar/search chrome if needed ── */
interface LayoutSlotState {
  sidebar: ReactNode | null;
  search: ReactNode | null;
}
interface LayoutContextValue {
  setSlots: (patch: Partial<LayoutSlotState>) => void;
  clearSlots: (keys: (keyof LayoutSlotState)[]) => void;
}
const LayoutContext = createContext<LayoutContextValue | undefined>(undefined);

export function useLayoutSlots(slots: Partial<LayoutSlotState>): void {
  const ctx = useContext(LayoutContext);
  if (!ctx) throw new Error('useLayoutSlots must be used within <AppLayout>');
  const { setSlots, clearSlots } = ctx;
  const { sidebar, search } = slots;
  useEffect(() => {
    const patch: Partial<LayoutSlotState> = {};
    if (sidebar !== undefined) patch.sidebar = sidebar;
    if (search !== undefined) patch.search = search;
    setSlots(patch);
    return () => clearSlots(Object.keys(patch) as (keyof LayoutSlotState)[]);
  }, [sidebar, search, setSlots, clearSlots]);
}

/* ── User menu (top-right) ── */
function UserMenu() {
  const { user, logout } = useAuth();
  const [open, setOpen] = useState(false);
  const ref = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent) {
      if (ref.current && !ref.current.contains(e.target as Node)) setOpen(false);
    }
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);
  if (!user) return null;
  const initials =
    user.name?.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase() ||
    user.email[0]?.toUpperCase();
  return (
    <div className="relative" ref={ref}>
      <button type="button" onClick={() => setOpen((v) => !v)} className="btn btn-ghost !px-2" aria-haspopup="menu" aria-expanded={open}>
        <span className="flex h-6 w-6 items-center justify-center rounded-full text-[11px] font-semibold" style={{ background: 'var(--accent)', color: 'var(--accent-ink)' }}>
          {initials}
        </span>
        <span className="hidden max-w-[10rem] truncate sm:inline">{user.name}</span>
        <ChevronDown size={14} className="opacity-60" />
      </button>
      {open && (
        <div role="menu" className="card absolute right-0 z-50 mt-1 w-56 overflow-hidden p-1 shadow-lg">
          <div className="px-3 py-2">
            <div className="truncate text-sm font-medium">{user.name}</div>
            <div className="truncate text-xs text-ink-faint">{user.email}</div>
          </div>
          <div className="my-1 h-px" style={{ background: 'var(--border)' }} />
          {user.isAdmin && (
            <Link to="/admin" role="menuitem" onClick={() => setOpen(false)} className="flex items-center gap-2 rounded px-3 py-2 text-sm hover:bg-surface-3">
              <Shield size={15} className="text-ink-muted" /> Admin
            </Link>
          )}
          <button type="button" role="menuitem" onClick={() => { setOpen(false); void logout(); }} className="flex w-full items-center gap-2 rounded px-3 py-2 text-left text-sm hover:bg-surface-3">
            <LogOut size={15} className="text-ink-muted" /> Log out
          </button>
        </div>
      )}
    </div>
  );
}

/* ── Resizable sidebar width, persisted per browser ── */
const SIDEBAR_WIDTH_KEY = 'str-wiki:sidebar-width';
const SIDEBAR_DEFAULT = 336; // ~30% wider than the old fixed 256px (w-64)
const SIDEBAR_MIN = 220;
const SIDEBAR_MAX = 620;

function clampWidth(n: number): number {
  return Math.min(SIDEBAR_MAX, Math.max(SIDEBAR_MIN, Math.round(n)));
}

function loadSidebarWidth(): number {
  try {
    const raw = localStorage.getItem(SIDEBAR_WIDTH_KEY);
    if (raw) {
      const n = parseInt(raw, 10);
      if (Number.isFinite(n)) return clampWidth(n);
    }
  } catch {
    /* ignore unreadable storage */
  }
  return SIDEBAR_DEFAULT;
}

/* ── AppLayout: top bar (brand + search + user) + left sidebar + Cmd-K palette ── */
function AppLayout() {
  const [slots, setSlotState] = useState<LayoutSlotState>({ sidebar: null, search: null });

  // Sidebar width: drag the divider to resize, double-click it to reset. The drag is
  // tracked by a full-screen overlay rather than window listeners so that passing the
  // cursor over a page's <iframe> can't swallow the pointer stream mid-drag.
  const [sidebarWidth, setSidebarWidth] = useState(loadSidebarWidth);
  const [resizing, setResizing] = useState(false);
  const resizeStart = useRef<{ x: number; w: number } | null>(null);

  useEffect(() => {
    try {
      localStorage.setItem(SIDEBAR_WIDTH_KEY, String(sidebarWidth));
    } catch {
      /* ignore unwritable storage */
    }
  }, [sidebarWidth]);

  const beginResize = (e: React.PointerEvent) => {
    e.preventDefault();
    resizeStart.current = { x: e.clientX, w: sidebarWidth };
    setResizing(true);
  };
  const onResizeMove = (e: React.PointerEvent) => {
    if (!resizeStart.current) return;
    setSidebarWidth(clampWidth(resizeStart.current.w + (e.clientX - resizeStart.current.x)));
  };
  const endResize = () => {
    resizeStart.current = null;
    setResizing(false);
  };

  const ctx = useMemo<LayoutContextValue>(
    () => ({
      setSlots: (patch) => setSlotState((prev) => ({ ...prev, ...patch })),
      clearSlots: (keys) =>
        setSlotState((prev) => {
          const next = { ...prev };
          for (const k of keys) next[k] = null;
          return next;
        }),
    }),
    [],
  );
  return (
    <TreeProvider>
      <LayoutContext.Provider value={ctx}>
        <div className="flex h-screen flex-col bg-surface-2 text-ink">
          <header className="flex h-12 flex-shrink-0 items-center gap-4 border-b px-4" style={{ borderColor: 'var(--border)', background: 'var(--surface)' }}>
            <Link to="/" className="flex items-center gap-2 font-semibold">
              <BookOpen size={18} className="text-accent" />
              <span className="hidden sm:inline">STR Wiki</span>
            </Link>
            <div className="mx-auto w-full max-w-xl">{slots.search ?? <SearchTrigger />}</div>
            <UserMenu />
          </header>
          <div className="flex min-h-0 flex-1">
            <aside
              className="hidden flex-shrink-0 overflow-y-auto md:block"
              style={{ width: `${sidebarWidth}px`, background: 'var(--surface-2)' }}
            >
              {slots.sidebar ?? <Sidebar />}
            </aside>
            {/* Resize divider — doubles as the left border of the main pane. */}
            <div
              role="separator"
              aria-orientation="vertical"
              onPointerDown={beginResize}
              onDoubleClick={() => setSidebarWidth(SIDEBAR_DEFAULT)}
              title="Drag to resize · double-click to reset"
              className={`hidden w-1.5 flex-shrink-0 cursor-col-resize border-l transition-colors md:block ${
                resizing ? 'bg-accent/60' : 'hover:bg-accent/30'
              }`}
              style={{ borderColor: 'var(--border)' }}
            />
            <main className="min-w-0 flex-1 overflow-y-auto">
              <Outlet />
            </main>
          </div>
        </div>
        {/* Full-screen capture layer, mounted only while dragging the divider. */}
        {resizing && (
          <div
            className="fixed inset-0 z-50 cursor-col-resize select-none"
            onPointerMove={onResizeMove}
            onPointerUp={endResize}
            onPointerLeave={endResize}
          />
        )}
        {/* Global Cmd-K palette (owns the keyboard listener + overlay) */}
        <SearchPalette />
      </LayoutContext.Provider>
    </TreeProvider>
  );
}

/* ── RequireAuth ── */
function RequireAuth({ children }: { children: ReactNode }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading)
    return (
      <div className="flex h-screen items-center justify-center bg-surface-2 text-ink-faint">
        <div className="animate-pulse text-sm">Loading…</div>
      </div>
    );
  if (!user) return <Navigate to="/login" replace state={{ from: location }} />;
  return <>{children}</>;
}

function NotFound() {
  return (
    <div className="mx-auto max-w-3xl p-8">
      <h1 className="mb-2 text-xl font-semibold">Not found</h1>
      <Link to="/" className="text-sm text-accent underline">Back to feed</Link>
    </div>
  );
}

export default function App() {
  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/forgot" element={<ForgotPassword />} />
      <Route path="/reset" element={<ResetPassword />} />
      <Route element={<RequireAuth><AppLayout /></RequireAuth>}>
        <Route path="/" element={<Feed />} />
        <Route path="/doc/:id" element={<DocView />} />
        <Route path="/doc/:id/edit" element={<DocEdit />} />
        <Route path="/admin" element={<Admin />} />
        <Route path="*" element={<NotFound />} />
      </Route>
    </Routes>
  );
}
